import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  User,
  Mail,
  Phone,
  Camera,
  Upload,
  Loader2,
  CheckCircle2,
  Link2,
  Trash2,
  Building2,
} from 'lucide-react';
import { useVendorAuth } from '../../context/VendorAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGE_SIZE_MB = 2;
const MAX_DESC_LENGTH = 500;

const VendorProfile = () => {
  const { vendor, updateVendor } = useVendorAuth();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    profile_image: '',
    description: '',
  });
  const [imageInputMode, setImageInputMode] = useState('upload'); // 'upload' | 'url'
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (vendor) {
      setForm({
        name: vendor.name || '',
        profile_image: vendor.profile_image || '',
        description: vendor.description || '',
      });
    }
  }, [vendor]);

  // Auto-hide success message
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(false), 2800);
      return () => clearTimeout(t);
    }
  }, [success]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      setError(`Image must be under ${MAX_IMAGE_SIZE_MB}MB.`);
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((prev) => ({ ...prev, profile_image: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleApplyUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    setError('');
    setForm((prev) => ({ ...prev, profile_image: url }));
    setImageUrlInput('');
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, profile_image: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/vendor/auth/profile`,
        {
          name: form.name.trim(),
          profile_image: form.profile_image,
          description: form.description,
        },
        { withCredentials: true }
      );
      updateVendor(res.data);
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Could not update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const initials =
    (vendor?.name || '')
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'V';

  const descRemaining = MAX_DESC_LENGTH - (form.description?.length || 0);

  return (
    <div data-testid="vendor-profile-page" className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">My Profile</h1>
        <p className="font-body text-text-secondary">
          Update your photo, name and short description. This is how customers will see you.
        </p>
      </div>

      {success && (
        <div
          className="mb-5 flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3"
          data-testid="profile-success"
        >
          <CheckCircle2 size={18} />
          <span className="font-body text-sm font-medium">Profile updated successfully</span>
        </div>
      )}

      {error && (
        <div
          className="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 font-body text-sm"
          data-testid="profile-error"
        >
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Avatar */}
        <div className="bg-white rounded-2xl border border-surface-border p-6">
          <h2 className="font-heading text-lg font-semibold text-text-primary mb-4">
            Profile Photo
          </h2>

          {/* Avatar preview */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-36 h-36 rounded-full border-4 border-white shadow-md ring-1 ring-surface-border overflow-hidden bg-brand-primary/10 flex items-center justify-center">
                {form.profile_image ? (
                  <img
                    src={form.profile_image}
                    alt={vendor?.name}
                    className="w-full h-full object-cover"
                    data-testid="profile-avatar-preview"
                  />
                ) : (
                  <span className="font-heading text-4xl font-bold text-brand-primary">
                    {initials}
                  </span>
                )}
              </div>
              {form.profile_image && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-white shadow-md border border-surface-border flex items-center justify-center hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                  aria-label="Remove image"
                  data-testid="profile-remove-image"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <p className="text-xs text-text-muted text-center">
              JPG, PNG or GIF. Max {MAX_IMAGE_SIZE_MB}MB.
            </p>
          </div>

          {/* Mode tabs */}
          <div className="mt-6 flex bg-background-secondary rounded-xl p-1 text-sm">
            <button
              type="button"
              onClick={() => setImageInputMode('upload')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-body font-semibold transition-colors ${
                imageInputMode === 'upload'
                  ? 'bg-white text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="profile-mode-upload"
            >
              <Upload size={14} />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setImageInputMode('url')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-body font-semibold transition-colors ${
                imageInputMode === 'url'
                  ? 'bg-white text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="profile-mode-url"
            >
              <Link2 size={14} />
              URL
            </button>
          </div>

          <div className="mt-4">
            {imageInputMode === 'upload' ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="profile-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-xl"
                  data-testid="profile-upload-btn"
                >
                  <Camera className="mr-2" size={16} />
                  Choose photo
                </Button>
              </>
            ) : (
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com/photo.jpg"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  className="rounded-xl"
                  data-testid="profile-url-input"
                />
                <Button
                  type="button"
                  onClick={handleApplyUrl}
                  className="rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white shrink-0"
                  data-testid="profile-url-apply"
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Details */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-border p-6 space-y-5">
          <h2 className="font-heading text-lg font-semibold text-text-primary">Basic Details</h2>

          {/* Name */}
          <div>
            <Label htmlFor="name" className="font-body font-medium mb-1.5 flex items-center gap-1.5">
              <User size={14} className="text-text-muted" />
              Full Name
            </Label>
            <Input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="rounded-xl"
              data-testid="profile-name-input"
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <Label className="font-body font-medium mb-1.5 flex items-center gap-1.5">
              <Mail size={14} className="text-text-muted" />
              Email
            </Label>
            <div
              className="rounded-xl bg-background-secondary border border-surface-border px-3 py-2.5 text-text-secondary font-body text-sm flex items-center justify-between"
              data-testid="profile-email"
            >
              <span className="truncate">{vendor?.email || '—'}</span>
              <span className="text-xs text-text-muted ml-3 shrink-0">Cannot be changed</span>
            </div>
          </div>

          {/* Phone (read-only if present) */}
          {vendor?.phone && (
            <div>
              <Label className="font-body font-medium mb-1.5 flex items-center gap-1.5">
                <Phone size={14} className="text-text-muted" />
                Phone
              </Label>
              <div
                className="rounded-xl bg-background-secondary border border-surface-border px-3 py-2.5 text-text-secondary font-body text-sm"
                data-testid="profile-phone"
              >
                {vendor.phone}
              </div>
            </div>
          )}

          {/* Apartment (read-only, meta info) */}
          {vendor?.apartment_id && (
            <div>
              <Label className="font-body font-medium mb-1.5 flex items-center gap-1.5">
                <Building2 size={14} className="text-text-muted" />
                Apartment
              </Label>
              <div className="rounded-xl bg-background-secondary border border-surface-border px-3 py-2.5 text-text-secondary font-body text-xs">
                Linked (managed by admin)
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label htmlFor="description" className="font-body font-medium mb-1.5">
              Short Description
            </Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => {
                const val = e.target.value.slice(0, MAX_DESC_LENGTH);
                setForm({ ...form, description: val });
              }}
              placeholder="Tell your neighbors about what you sell, your speciality, or anything you'd like them to know..."
              rows={5}
              className="rounded-xl resize-none"
              data-testid="profile-description-input"
            />
            <div className="flex justify-end mt-1.5">
              <span
                className={`font-body text-xs ${
                  descRemaining < 40 ? 'text-amber-600' : 'text-text-muted'
                }`}
              >
                {descRemaining} characters left
              </span>
            </div>
          </div>

          {/* Save */}
          <div className="pt-2 flex items-center justify-end gap-3 border-t border-surface-border">
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white font-semibold px-6"
              data-testid="profile-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorProfile;
